import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SWORN_TRANSLATOR_PRODUCTS, SWORN_PRODUCT_CATEGORIES, SWORN_CATALOG_PRICE_MIN, SWORN_CATALOG_PRICE_MAX, SWORN_TURNAROUND_STD } from "@/data/sworn-translator-products";
import { BRAND_URL } from "@/lib/brand";
import { ArrowRight, Languages, ShieldCheck, Clock } from "lucide-react";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const PATH = "/en/sworn-translator/products";
const URL = `${BRAND_URL}${PATH}`;

export const Route = createFileRoute("/en/sworn-translator/products/")({
  head: () => ({
    meta: [
      { title: ctaTitle("Sworn Translation — Bangkok Catalog | NYC", "/en/sworn-translator/products/index", "en")},
      { name: "description", content: ctaDesc("Sworn translation catalog: Thai MoJ translators + destination sworn network (DE/FR/ES/IT/NL/PL). Start with a free review before you file anything.", "/en/sworn-translator/products/index", "en")},
      { property: "og:title", content: ctaTitle("Sworn Translation Catalog | NYC Language Institute", "/en/sworn-translator/products/index", "en")},
      { property: "og:description", content: ctaDesc("20+ languages · MoJ translator + destination sworn network · bundle notary + MFA + Apostille.", "/en/sworn-translator/products/index", "en")},
      { property: "og:url", content: URL },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: `${BRAND_URL}/sworn-translator/products` },
      { rel: "alternate", hrefLang: "x-default", href: `${BRAND_URL}/sworn-translator/products` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "ItemList",
        "@id": `${URL}#catalog`,
        name: "Sworn Translation Product Catalog",
        numberOfItems: SWORN_TRANSLATOR_PRODUCTS.length,
        itemListElement: SWORN_TRANSLATOR_PRODUCTS.map((p, i) => ({
          "@type": "ListItem", position: i + 1,
          url: `${BRAND_URL}/en/sworn-translator/products/${p.slug}`,
          name: p.nameEn,
        })),
      })},
    ],
  }),
  component: () => (
    <main className="min-h-screen bg-background pb-20">
      <section className="mx-auto max-w-6xl px-4 pt-10">
        <div className="flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest text-purple-700">
          <span className="rounded-full bg-purple-100 px-3 py-1">Sworn Translator · MoJ · Destination sworn</span>
        </div>
        <h1 className="mt-3 font-display text-4xl font-extrabold text-navy-deep sm:text-5xl">
          Sworn Translation Services
        </h1>
        <p className="mt-3 max-w-3xl text-lg text-slate-600">
          {SWORN_TRANSLATOR_PRODUCTS.length} SKUs — Thai MoJ-registered translators + a destination sworn-translator network (Germany §189 GVG · France Cour d'appel · Spain MAEC · Italy Tribunale · Netherlands Wbtv · Poland tłumacz przysięgły). <strong>Bundle notary + MFA + destination Apostille under one roof.</strong>
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
            <ShieldCheck className="mb-2 h-5 w-5 text-emerald-700" />
            <div className="font-display font-bold text-emerald-900">MoJ-registered translator</div>
            <div className="text-xs text-slate-600">Thai Ministry of Justice roster</div>
          </div>
          <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-4">
            <Clock className="mb-2 h-5 w-5 text-purple-700" />
            <div className="font-display font-bold text-purple-900">{SWORN_TURNAROUND_STD}</div>
            <div className="text-xs text-slate-600">Standard · 24-hour express</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-4">
            <Languages className="mb-2 h-5 w-5 text-slate-700" />
            <div className="font-display font-bold text-slate-900">20+ languages</div>
            <div className="text-xs text-slate-600">EN/ZH/JA/KO/DE/FR/ES/IT/AR + more</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          {SWORN_PRODUCT_CATEGORIES.map((c) => (
            <a key={c.id} href={`#cat-${c.id}`} className="rounded-full border border-purple-200 bg-white px-3 py-1.5 font-semibold text-purple-800 hover:bg-purple-50">
              {c.labelEn} <span className="opacity-60">({c.count})</span>
            </a>
          ))}
        </div>
      </section>

      {SWORN_PRODUCT_CATEGORIES.map((c) => {
        const items = SWORN_TRANSLATOR_PRODUCTS.filter((p) => p.category === c.id);
        return (
          <section key={c.id} id={`cat-${c.id}`} className="mx-auto mt-12 max-w-6xl px-4">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.labelEn}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Link
                  key={p.sku}
                  to="/en/sworn-translator/products/$slug"
                  params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-purple-400 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-purple-700">{p.categoryLabelEn}</div>
                  <div className="mt-1 font-display text-lg font-extrabold text-navy-deep group-hover:text-purple-700">{p.nameEn}</div>
                  <div className="mt-1 text-xs text-slate-500">{p.nameTh}</div>
                  <div className="mt-3 font-display text-lg font-extrabold text-emerald-800">
                    THB {p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500">{p.turnaround}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-purple-700">
                    View details <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
      <ProductCatalogHubNav locale="en" current="sworn" />
    <HubLeadCTA locale="en" service="Sworn Translator" />
      </main>
  ),
});
